import type { Mixpanel } from 'mixpanel-browser';

import { MixpanelAnalyticsBuffer, type MixpanelAnalyticsContext } from './MixpanelAnalyticsBuffer';

export type MixpanelReplaySdk = Pick<
  Mixpanel,
  | 'get_distinct_id'
  | 'identify'
  | 'reset'
  | 'register'
  | 'unregister'
  | 'start_session_recording'
  | 'stop_session_recording'
>;

export interface MixpanelReplayIdentity {
  readonly distinctId: string;
  readonly sessionId: string | null;
}

export interface MixpanelReplayDecision {
  readonly distinctId: string;
  readonly sessionId: string;
  readonly enabled: unknown;
}

export interface MixpanelReplayContext {
  readonly identity: MixpanelReplayIdentity | null;
  readonly decision: MixpanelReplayDecision | null;
  readonly providerId: number | null;
  readonly captureAllowed: boolean;
}

interface DesiredState extends MixpanelAnalyticsContext {
  readonly enabled: boolean;
}

type StopOutcome = { ok: true } | { ok: false; error: unknown };

function sameIdentity(
  left: MixpanelReplayIdentity | null,
  right: MixpanelReplayIdentity | null,
): boolean {
  return left?.distinctId === right?.distinctId && left?.sessionId === right?.sessionId;
}

function canRecord(context: MixpanelReplayContext): boolean {
  const { identity, decision } = context;
  return (
    context.captureAllowed &&
    identity !== null &&
    identity.distinctId.length > 0 &&
    identity.sessionId !== null &&
    identity.sessionId.length > 0 &&
    decision !== null &&
    decision.distinctId === identity.distinctId &&
    decision.sessionId === identity.sessionId &&
    decision.enabled === true
  );
}

export class MixpanelReplayController {
  private desired: DesiredState | null = null;
  private appliedIdentity: MixpanelReplayIdentity | null = null;
  private appliedProviderId: number | null = null;
  private identityApplied = false;
  private identityReady = false;
  private recording = false;
  private needsStop = true;
  private failed = false;
  private revision = 0;
  private queue: Promise<void> = Promise.resolve();
  private transition: Promise<void> = this.queue;
  private readonly messages: MixpanelAnalyticsBuffer;

  constructor(private readonly sdk: MixpanelReplaySdk, onDiscard: () => void = () => undefined) {
    this.messages = new MixpanelAnalyticsBuffer(onDiscard);
  }

  public suspend(): Promise<void> {
    ++this.revision;
    this.desired = null;
    this.identityReady = false;
    this.failed = false;
    const pendingStop = this.stopNow();
    const cutoff = this.messages.lastSequence;
    const operation = this.queue.then(async () => {
      this.identityReady = false;
      this.messages.discardThrough(cutoff);
      await this.stopCapture(pendingStop);
    });
    this.transition = operation;
    this.queue = operation.catch(() => undefined);
    return operation;
  }

  public update(context: MixpanelReplayContext): Promise<void> {
    const next: DesiredState = {
      identity: context.identity === null ? null : { ...context.identity },
      providerId: context.providerId,
      enabled: canRecord(context),
    };
    const identityChanged = !sameIdentity(this.desired?.identity ?? null, next.identity);
    if (
      this.desired !== null &&
      !identityChanged &&
      this.desired.providerId === next.providerId &&
      this.desired.enabled === next.enabled &&
      !this.failed
    )
      return this.transition;

    const revision = ++this.revision;
    this.desired = next;
    this.failed = false;
    this.messages.bindUnknown(next);
    const cutoff = this.messages.lastSequence;
    const pendingStop = this.needsStop || identityChanged || !next.enabled ? this.stopNow() : null;
    const operation = this.queue
      .then(async () => {
        this.identityReady = false;
        if (pendingStop !== null) await this.stopCapture(pendingStop);
        else if (this.needsStop) await this.stopCapture(this.stopNow());
        await this.applyIdentity(next.identity);
        this.applyProvider(next.providerId);
        this.identityReady = true;
        await this.startReplay(next, revision);
      })
      .catch(async (error: unknown) => {
        if (revision === this.revision) this.failed = true;
        const cleanup = await this.stopNow();
        if (!cleanup.ok) {
          this.identityReady = false;
          throw new AggregateError([error, cleanup.error], 'Replay transition and cleanup failed');
        }
        throw error;
      });

    this.transition = operation;
    this.queue = operation.catch(() => undefined);
    this.scheduleMessages(next, cutoff);
    return operation;
  }

  public runAfterTransition(action: () => void): Promise<void> {
    const context = this.desired;
    const completion = this.messages.enqueue(context, action);
    if (context !== null) this.scheduleMessages(context, this.messages.lastSequence);
    return completion;
  }

  private scheduleMessages(context: MixpanelAnalyticsContext, cutoff: number): void {
    this.queue = this.queue.then(() => {
      if (
        this.identityReady &&
        sameIdentity(this.appliedIdentity, context.identity) &&
        this.appliedProviderId === context.providerId
      )
        this.messages.drain(context, cutoff);
    });
  }

  private async applyIdentity(next: MixpanelReplayIdentity | null): Promise<void> {
    if (this.identityApplied && sameIdentity(this.appliedIdentity, next)) return;
    const savedId: unknown = this.identityApplied ? null : this.sdk.get_distinct_id();
    if (!this.identityApplied && (typeof savedId !== 'string' || savedId.length === 0)) {
      throw new Error('Mixpanel saved analytics identity is unavailable');
    }
    const savedAccountChanged =
      typeof savedId === 'string' &&
      savedId.startsWith('provider-portal-account-') &&
      savedId !== next?.distinctId;
    if (next === null || this.appliedIdentity !== null || savedAccountChanged) {
      this.sdk.reset();
      await this.stopCapture(this.stopNow());
    }
    if (next !== null) this.sdk.identify(next.distinctId);
    this.appliedIdentity = next;
    this.identityApplied = true;
  }

  private applyProvider(providerId: number | null): void {
    if (providerId === null) this.sdk.unregister('providerId');
    else this.sdk.register({ providerId });
    this.appliedProviderId = providerId;
  }

  private async startReplay(next: DesiredState, revision: number): Promise<void> {
    if (revision !== this.revision || !next.enabled || this.recording) return;
    await this.sdk.start_session_recording();
    if (revision === this.revision) this.recording = true;
  }

  private stopNow(): Promise<StopOutcome> {
    this.recording = false;
    this.needsStop = true;
    try {
      return this.sdk.stop_session_recording().then<StopOutcome, StopOutcome>(
        () => ({ ok: true }),
        (error: unknown) => ({ ok: false, error }),
      );
    } catch (error: unknown) {
      return Promise.resolve({ ok: false, error });
    }
  }

  private async stopCapture(stop: Promise<StopOutcome>): Promise<void> {
    const outcome = await stop;
    if (!outcome.ok) throw outcome.error;
    this.needsStop = false;
  }
}
